import { useEffect, useState } from "react";
import { Upload, Trash2, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../api";
import { Card, PageHeader } from "../ui";
import { useAuth } from "../AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AdminMinhaAssinatura() {
  const { user, refresh } = useAuth();
  const [has, setHas] = useState(!!user?.has_signature);
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState(Date.now());

  useEffect(() => {
    if (user) setHas(!!user.has_signature);
  }, [user]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) {
      toast.error("A imagem não pode ultrapassar 3MB.");
      return;
    }
    const fd = new FormData();
    fd.append("file", f);
    setBusy(true);
    try {
      await api.post("/admin/me/signature", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Assinatura salva!");
      setHas(true);
      setToken(Date.now());
      if (refresh) await refresh();
    } catch (err) {
      toast.error("Falha ao salvar assinatura.");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const remove = async () => {
    if (!window.confirm("Remover sua assinatura?")) return;
    setBusy(true);
    try {
      await api.delete("/admin/me/signature");
      toast.success("Assinatura removida.");
      setHas(false);
      if (refresh) await refresh();
    } catch (err) {
      toast.error("Falha ao remover.");
    } finally {
      setBusy(false);
    }
  };

  const isDir = user?.role === "diretor_financeiro";

  return (
    <div data-testid="admin-minha-assinatura">
      <PageHeader
        title="Minha Assinatura"
        subtitle={
          isDir
            ? "Faça upload da sua assinatura digital. Ela será inserida automaticamente nos recibos que você emitir como Diretor Financeiro."
            : "Faça upload da sua assinatura digital. Ela será inserida nos recibos que você emitir."
        }
      />

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="font-serif-display text-xl mb-4">Sua assinatura atual</h3>
          {has ? (
            <div className="space-y-4">
              <div className="border border-slate-200 rounded-sm p-4 bg-slate-50 grid place-items-center min-h-[160px]">
                <img
                  src={`${BACKEND}/api/admin/me/signature?t=${token}`}
                  alt="Assinatura"
                  className="max-h-32 object-contain"
                />
              </div>
              <div className="inline-flex items-center gap-2 text-sm text-emerald-700">
                <CheckCircle2 size={14} /> Assinatura cadastrada — aparecerá nos
                novos recibos.
              </div>
              <div className="flex gap-2">
                <label
                  data-testid="sig-upload"
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#0A0F14] text-white text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776] cursor-pointer"
                >
                  <Upload size={14} />
                  {busy ? "Enviando..." : "Substituir"}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={onFile}
                    className="hidden"
                  />
                </label>
                <button
                  data-testid="sig-delete"
                  onClick={remove}
                  disabled={busy}
                  className="inline-flex items-center gap-2 px-4 py-2.5 border border-rose-300 text-rose-700 text-[11px] uppercase tracking-[0.22em] hover:bg-rose-50"
                >
                  <Trash2 size={14} /> Remover
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="border-2 border-dashed border-slate-300 rounded-sm p-10 text-center bg-slate-50">
                <Upload size={28} className="mx-auto text-slate-400 mb-2" />
                <p className="text-sm text-slate-500">
                  Nenhuma assinatura cadastrada.
                </p>
              </div>
              <label
                data-testid="sig-upload"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0A0F14] text-white text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776] cursor-pointer"
              >
                <Upload size={14} />
                {busy ? "Enviando..." : "Fazer upload da assinatura"}
                <input
                  type="file"
                  accept="image/*"
                  onChange={onFile}
                  className="hidden"
                />
              </label>
            </div>
          )}
        </Card>

        <Card className="p-6 bg-[#FEDF00]/15 border-[#FEDF00]/40">
          <div className="flex gap-3">
            <AlertCircle size={20} className="text-[#002776] shrink-0 mt-0.5" />
            <div>
              <h4 className="font-serif-display text-lg">
                Dicas para uma boa assinatura
              </h4>
              <ul className="mt-3 space-y-1.5 text-sm text-slate-700 list-disc pl-4">
                <li>
                  Use uma imagem PNG <strong>com fundo transparente</strong> para
                  melhor encaixe no recibo.
                </li>
                <li>
                  Resolução recomendada: 600×200 pixels ou superior.
                </li>
                <li>Tamanho máximo do arquivo: 3MB.</li>
                <li>Formatos aceitos: PNG, JPG, WebP.</li>
                <li>
                  A assinatura aparece logo acima do seu nome e cargo no recibo,
                  centralizada.
                </li>
              </ul>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
